import { type FC } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'react-relay/legacy';
import {
    SharedRelatedItemsWrapper,
    type ChildrenProps as GridProps,
} from '../../shared/SharedRelatedItemsWrapper/SharedRelatedItemsWrapper';
import { SharedMobileRelatedItem } from '../../shared/SharedMobileRelatedItem/SharedMobileRelatedItem';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import {
    trackItemsImpression,
    trackItemClick,
    type Item as ItemTrackProps,
} from '../../shared/SharedRelatedItemsWrapper/helpers/sharedRelatedItemsWrapperTracking';
import { peopleAlsoBrowsed } from '../../shared/sharedMessages';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { RESULTS_FIRST_ROW_COUNT } from '../../constants/sbConstants';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { type BuySharedPersonalizedSearchItems_viewer$key } from './__generated__/BuySharedPersonalizedSearchItems_viewer.graphql';
import { type BuySharedPersonalizedSearchItems_itemSearch$key } from './__generated__/BuySharedPersonalizedSearchItems_itemSearch.graphql';
import { type BuySharedPersonalizedSearchItems_user$key } from './__generated__/BuySharedPersonalizedSearchItems_user.graphql';

type Props = {
    viewer: BuySharedPersonalizedSearchItems_viewer$key;
    itemSearch: BuySharedPersonalizedSearchItems_itemSearch$key;
    user: BuySharedPersonalizedSearchItems_user$key;
    isMobile: boolean;
    isClient: boolean;
};

export const BuySharedPersonalizedSearchItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    isMobile,
    isClient,
}) => {
    const viewer = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_viewer on Viewer {
                ...SharedItemTile_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_itemSearch on ItemSearchQueryConnection
            @argumentDefinitions(priceBookName: { type: "String" }) {
                ...SharedItemTile_itemSearch
                personalizedSearchItems(first: 28) {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SharedMobileRelatedItem_item
                                    @arguments(priceBookName: $priceBookName)
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...sharedRelatedItemsWrapperTracking_item
                                ...SharedRelatedItemsWrapper_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const user = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_user on User {
                ...SharedRelatedItemsWrapper_user
                ...SharedMobileRelatedItem_user
                ...SharedItemTile_user
            }
        `,
        userRef
    );

    const { personalizedSearchItems } = itemSearch;
    const items =
        personalizedSearchItems?.edges?.map(edge => edge?.node?.item).filter(filterFalsy) || [];
    const userRegions = useUserSessionRegions() || [];

    const onItemClick = ({ item, index }: { item: ItemTrackProps; index: number }): void => {
        trackItemClick({
            items: [item],
            startIndex: index,
            list: `Buy personalized search items`,
            userRegions,
        });
    };

    if (!items.length) {
        return null;
    }

    return (
        <SharedRelatedItemsWrapper
            user={user}
            header={peopleAlsoBrowsed}
            isMobile={isMobile}
            isClient={isClient}
            items={items}
            showSwiper={isMobile}
            initialCount={RESULTS_FIRST_ROW_COUNT}
            onItemsImpression={({ startIndex, itemsLengthToShow }) => {
                if (items?.length) {
                    trackItemsImpression({
                        items: [...items].splice(startIndex, itemsLengthToShow),
                        startIndex,
                        list: `Buy personalized search items`,
                        userRegions,
                    });
                }
            }}
        >
            {({ itemClassName, ...itemProps }: GridProps) =>
                items.map((item, index) => {
                    return isMobile ? (
                        <SharedMobileRelatedItem
                            key={item.serviceId}
                            index={index}
                            item={item}
                            user={user}
                            ymalHeartsEnabled
                            itemsPerRow={0}
                            onClick={() => onItemClick({ item, index })}
                            {...itemProps}
                            {...itemClassName(index)}
                            isClient={isClient}
                            imageBackgroundColor="moonStone"
                        />
                    ) : (
                        <SharedItemTile
                            key={item.serviceId}
                            index={index}
                            viewer={viewer}
                            item={item}
                            itemSearch={itemSearch}
                            user={user}
                            showCreatorName
                            showSellerName={false}
                            fetchFolderOnMouseOver
                            lazyLoadStartIndex={0}
                            onClick={() => onItemClick({ item, index })}
                            mlFreightQuotesAbTestVariant={null}
                            {...itemProps}
                            {...itemClassName(index)}
                        />
                    );
                })
            }
        </SharedRelatedItemsWrapper>
    );
};
